import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { superAdminAPI } from "../../services/api";
import toast from "react-hot-toast";
import { Card, Button, Spinner } from "../../components/UI";
import GoogleCalendarSection from "../../components/UI/GoogleCalendarSection";
import GoogleMeetSection from "../../components/UI/GoogleMeetSection";
import AICallAnalysisSettings from "../../components/SuperAdmin/AICallAnalysisSettings";
import ReferAndEarnSettings from "../../components/SuperAdmin/ReferAndEarnSettings";
import EmailProviderSettings from "../../components/SuperAdmin/EmailProviderSettings";
import RazorpaySettings from "../../components/SuperAdmin/RazorpaySettings";
import CompanyAccountSettings from "../../components/SuperAdmin/CompanyAccountSettings";
import {
  HiOutlineCog,
  HiOutlinePhone,
  HiOutlineSparkles,
  HiOutlineSearch,
  HiOutlineCursorClick,
  HiOutlineRefresh,
  HiOutlineCalendar,
  HiOutlineGift,
  HiOutlineMail,
  HiOutlineCreditCard,
  HiOutlineOfficeBuilding,
} from "react-icons/hi";

/**
 * SuperAdmin → Settings
 * Two sections (Call Limits open by default, Calendar collapsed):
 *
 * 1. Call Limits — per-organization control of the direct (automatic) call
 *    analysis: mode (Manual / Direct) and the daily direct-analysis limit.
 *    The admin panel's Settings → Calls section reads these values
 *    (admins can only switch the mode; the limit is superadmin-controlled).
 * 2. Calendar — synchronizes CRM follow-ups and scheduled activities with
 *    Google Calendar (embedded Google Calendar + private ICS feed).
 * 3. AI Call Analysis — which AI analyzes call recordings (Claude default,
 *    Ollama alternative), Claude model/API key and Ollama server/model.
 */

const ModeBadge = ({ mode }) => (
  <span
    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
      mode === "auto"
        ? "bg-purple-100 text-purple-700"
        : "bg-blue-100 text-blue-700"
    }`}
  >
    {mode === "auto" ? (
      <HiOutlineRefresh className="w-3.5 h-3.5" />
    ) : (
      <HiOutlineCursorClick className="w-3.5 h-3.5" />
    )}
    {mode === "auto" ? "Direct (auto)" : "Manual"}
  </span>
);

const SuperAdminSettings = () => {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  // Draft limit values per org id (edited inline before saving)
  const [drafts, setDrafts] = useState({});
  // Two sections shown side by side at the top — Call Limits active by default
  const [activeSection, setActiveSection] = useState("callLimits");

  // All organizations with their callAnalysis settings
  const { data, isLoading } = useQuery({
    queryKey: ["sa-settings-orgs", page, search],
    queryFn: async () => {
      const response = await superAdminAPI.getOrganizations({
        page,
        limit: 20,
        ...(search ? { search } : {}),
      });
      return response.data || {};
    },
  });

  const organizations = data?.data || [];
  const pagination = data?.pagination || {};

  const updateMutation = useMutation({
    mutationFn: ({ id, autoDailyLimit, callAnalysisMode }) =>
      superAdminAPI.updateOrganization(id, { autoDailyLimit, callAnalysisMode }),
    onSuccess: (response, variables) => {
      queryClient.invalidateQueries(["sa-settings-orgs"]);
      toast.success(
        `Call limit updated to ${variables.autoDailyLimit}/day (${variables.callAnalysisMode})`
      );
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to update call limit");
    },
  });

  const getLimit = (org) =>
    drafts[org._id]?.limit ?? org.callAnalysis?.autoDailyLimit ?? 10;
  const getMode = (org) =>
    drafts[org._id]?.mode ?? org.callAnalysis?.mode ?? "manual";

  const setDraft = (org, patch) =>
    setDrafts((prev) => ({
      ...prev,
      [org._id]: { ...(prev[org._id] || {}), ...patch },
    }));

  const saveOrg = (org) => {
    updateMutation.mutate({
      id: org._id,
      autoDailyLimit: Math.max(0, parseInt(getLimit(org)) || 0),
      callAnalysisMode: getMode(org),
    });
  };

  return (
    <div className="p-4 lg:p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
          <HiOutlineCog className="w-5 h-5 text-primary-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
          <p className="text-sm text-gray-500">
            Platform-wide settings — Call Limits, Calendar and AI Call Analysis
          </p>
        </div>
      </div>

      {/* ─── Sections side by side at the top ─── */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-3">
        <button
          type="button"
          onClick={() => setActiveSection("callLimits")}
          className={`
            flex items-center justify-center gap-2 px-4 py-3 rounded-xl border
            text-sm font-semibold transition-colors
            ${activeSection === "callLimits"
              ? "bg-purple-50 border-purple-500 text-purple-700 shadow-sm"
              : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"
            }
          `}
        >
          <HiOutlinePhone className="w-4 h-4" />
          Call Limits
        </button>
        <button
          type="button"
          onClick={() => setActiveSection("calendar")}
          className={`
            flex items-center justify-center gap-2 px-4 py-3 rounded-xl border
            text-sm font-semibold transition-colors
            ${activeSection === "calendar"
              ? "bg-blue-50 border-blue-500 text-blue-700 shadow-sm"
              : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"
            }
          `}
        >
          <HiOutlineCalendar className="w-4 h-4" />
          Calendar
        </button>
        <button
          type="button"
          onClick={() => setActiveSection("ai")}
          className={`
            flex items-center justify-center gap-2 px-4 py-3 rounded-xl border
            text-sm font-semibold transition-colors
            ${activeSection === "ai"
              ? "bg-purple-50 border-purple-500 text-purple-700 shadow-sm"
              : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"
            }
          `}
        >
          <HiOutlineSparkles className="w-4 h-4" />
          AI Call Analysis
        </button>
        <button
          type="button"
          onClick={() => setActiveSection("referEarn")}
          className={`
            flex items-center justify-center gap-2 px-4 py-3 rounded-xl border
            text-sm font-semibold transition-colors
            ${activeSection === "referEarn"
              ? "bg-green-50 border-green-500 text-green-700 shadow-sm"
              : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"
            }
          `}
        >
          <HiOutlineGift className="w-4 h-4" />
          Refer & Earn
        </button>
        <button
          type="button"
          onClick={() => setActiveSection("email")}
          className={`
            flex items-center justify-center gap-2 px-4 py-3 rounded-xl border
            text-sm font-semibold transition-colors
            ${activeSection === "email"
              ? "bg-purple-50 border-purple-500 text-purple-700 shadow-sm"
              : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"
            }
          `}
        >
          <HiOutlineMail className="w-4 h-4" />
          Email Provider
        </button>
        <button
          type="button"
          onClick={() => setActiveSection("razorpay")}
          className={`
            flex items-center justify-center gap-2 px-4 py-3 rounded-xl border
            text-sm font-semibold transition-colors
            ${activeSection === "razorpay"
              ? "bg-purple-50 border-purple-500 text-purple-700 shadow-sm"
              : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"
            }
          `}
        >
          <HiOutlineCreditCard className="w-4 h-4" />
          Razorpay
        </button>
        <button
          type="button"
          onClick={() => setActiveSection("company")}
          className={`
            flex items-center justify-center gap-2 px-4 py-3 rounded-xl border
            text-sm font-semibold transition-colors
            ${activeSection === "company"
              ? "bg-purple-50 border-purple-500 text-purple-700 shadow-sm"
              : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"
            }
          `}
        >
          <HiOutlineOfficeBuilding className="w-4 h-4" />
          Company Account
        </button>
      </div>

      {/* ─── Section 3: AI Call Analysis (Claude / Ollama) ─── */}
      {activeSection === "ai" && <AICallAnalysisSettings />}

      {/* ─── Section 4: Refer & Earn ─── */}
      {activeSection === "referEarn" && <ReferAndEarnSettings />}

      {/* ─── Section 5: Email Provider (SMTP / Brevo / Zepto) ─── */}
      {activeSection === "email" && <EmailProviderSettings />}

      {/* ─── Section 6: Razorpay ─── */}
      {activeSection === "razorpay" && <RazorpaySettings />}

      {/* ─── Section 7: Company Account (demo leads + subscription digest) ─── */}
      {activeSection === "company" && <CompanyAccountSettings />}

      {/* ─── Section 1: Call Limits (active by default) ─── */}
      {activeSection === "callLimits" && (
      <Card padding="p-4 sm:p-6" className="space-y-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0">
            <HiOutlinePhone className="w-5 h-5 text-purple-600" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-bold text-gray-900">Call Limits</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Set the <strong>direct (automatic) call analysis limit per day</strong> for each
              organization, and choose its analysis mode. Organizations see this in their
              Settings → Calls section but cannot change the limit — only you can.
            </p>
          </div>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <HiOutlineSearch className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Search organizations..."
            className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>

        {/* Org list */}
        {isLoading ? (
          <div className="py-12 flex justify-center">
            <Spinner size="lg" />
          </div>
        ) : organizations.length === 0 ? (
          <div className="py-12 text-center text-sm text-gray-500">
            No organizations found
          </div>
        ) : (
          <div className="divide-y divide-gray-100 border border-gray-100 rounded-xl">
            {organizations.map((org) => {
              const mode = getMode(org);
              const limit = getLimit(org);
              const dirty =
                limit !== (org.callAnalysis?.autoDailyLimit ?? 10) ||
                mode !== (org.callAnalysis?.mode ?? "manual");

              return (
                <div
                  key={org._id}
                  className="flex flex-col lg:flex-row lg:items-center gap-3 p-4"
                >
                  {/* Org info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-gray-900 text-sm truncate">
                        {org.name}
                      </p>
                      <ModeBadge mode={mode} />
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {org.planId?.name || "No plan"} · {org.usage?.users || 0} users ·{" "}
                      {org.email}
                    </p>
                  </div>

                  {/* Mode switch */}
                  <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => setDraft(org, { mode: "manual" })}
                      className={`px-2.5 py-1 rounded-md text-xs font-medium transition ${
                        mode === "manual"
                          ? "bg-white text-blue-700 shadow-sm"
                          : "text-gray-500 hover:text-gray-700"
                      }`}
                    >
                      Manual
                    </button>
                    <button
                      type="button"
                      onClick={() => setDraft(org, { mode: "auto" })}
                      className={`px-2.5 py-1 rounded-md text-xs font-medium transition ${
                        mode === "auto"
                          ? "bg-white text-purple-700 shadow-sm"
                          : "text-gray-500 hover:text-gray-700"
                      }`}
                    >
                      Direct
                    </button>
                  </div>

                  {/* Daily limit input */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <label className="text-xs text-gray-500 whitespace-nowrap">
                      Limit / day
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={limit}
                      onChange={(e) =>
                        setDraft(org, { limit: e.target.value })
                      }
                      className="w-20 px-2 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                    <Button
                      size="sm"
                      onClick={() => saveOrg(org)}
                      disabled={!dirty || updateMutation.isPending}
                      className="flex-shrink-0"
                    >
                      {updateMutation.isPending &&
                      updateMutation.variables?.id === org._id ? (
                        <Spinner size="sm" />
                      ) : (
                        "Save"
                      )}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-between pt-2">
            <span className="text-sm text-gray-500">
              {pagination.total} organizations
            </span>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
              >
                Previous
              </Button>
              <span className="text-sm text-gray-600">
                {page} / {pagination.totalPages}
              </span>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                disabled={page >= pagination.totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        )}

        {/* Info */}
        <div className="flex gap-3 bg-blue-50 border border-blue-200 rounded-xl p-4">
          <HiOutlineSparkles className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-blue-700 space-y-1">
            <p>
              <strong>Manual</strong> — the organization picks specific calls to analyze. No
              automatic analysis, no daily usage.
            </p>
            <p>
              <strong>Direct (auto)</strong> — recordings are analyzed automatically as they
              arrive, up to the limit per day. Beyond it, the organization can still analyze
              calls manually. Set <strong>0</strong> to disable direct analysis entirely.
            </p>
            <p>
              Changes take effect immediately for new recordings — the backend enforces the
              limit per organization per calendar day.
            </p>
          </div>
        </div>
      </Card>
      )}

      {/* ─── Section 2: Calendar ─── */}
      {activeSection === "calendar" && (
      <Card padding="p-4 sm:p-6" className="space-y-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
            <HiOutlineCalendar className="w-5 h-5 text-blue-600" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-bold text-gray-900">Calendar</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Synchronizes CRM follow-ups and scheduled activities with Google Calendar.
            </p>
          </div>
        </div>

        <GoogleCalendarSection
          scope="superadmin"
          fetchSettings={async () => {
            const response = await superAdminAPI.getCalendarSettings();
            return response?.data?.data || {};
          }}
          saveSettings={(googleCalendarId) =>
            superAdminAPI.updateCalendarSettings(googleCalendarId)
          }
        />

        <GoogleMeetSection
          scope="superadmin"
          listMeetings={(params) => superAdminAPI.getMeetings(params)}
          createMeeting={(body) => superAdminAPI.createMeeting(body)}
          updateMeeting={(id, body) => superAdminAPI.updateMeeting(id, body)}
          showOrganization
        />
      </Card>
      )}
    </div>
  );
};

export default SuperAdminSettings;